import { createApiCall } from 'lib/base';

// A new authenticator secret, confirmed with a code before 2FA is turned on.
export interface TwoFactorSetup {
  secret: string;
  otpauth_url: string;
}

// The password login response: either signed in (access_token) or a login
// challenge for the second step.
export interface AdminLoginResponse {
  access_token?: string;
  expires_at?: string;
  staff_account?: any;
  two_factor_required?: boolean;
  two_factor_setup_required?: boolean;
  login_challenge?: string;
  login_challenge_expires_at?: string;
  // Only when 2FA was just turned on at login; shown once.
  backup_codes?: string[];
}

interface LoginChallengeBody {
  login_challenge: string;
}

interface LoginChallengeCodeBody extends LoginChallengeBody {
  code: string;
}

interface TwoFactorCodeBody {
  code: string;
}

// Login steps run before there is a token: a 401/400 must not redirect, the
// page reads error.response.error_code instead.
export const loginVerifyTwoFactorApi = createApiCall<LoginChallengeCodeBody>(
  'LOGIN_VERIFY_TWO_FACTOR',
  body => ({
    endpoint: `api/admin/auth/login/two-factor`,
    method: 'POST',
    body,
    skipDefaultErrorHandling: true,
  })
);

export const loginSetupTwoFactorApi = createApiCall<LoginChallengeBody>(
  'LOGIN_SETUP_TWO_FACTOR',
  body => ({
    endpoint: `api/admin/auth/login/two-factor/setup`,
    method: 'POST',
    body,
    skipDefaultErrorHandling: true,
  })
);

export const loginEnableTwoFactorApi = createApiCall<LoginChallengeCodeBody>(
  'LOGIN_ENABLE_TWO_FACTOR',
  body => ({
    endpoint: `api/admin/auth/login/two-factor/enable`,
    method: 'POST',
    body,
    skipDefaultErrorHandling: true,
  })
);

// Signed-in account settings; errors are shown by the page in Vietnamese.
export const setupTwoFactorApi = createApiCall<void>(
  'SETUP_TWO_FACTOR',
  () => ({
    endpoint: `api/admin/account/two-factor/setup`,
    method: 'POST',
    skipDefaultErrorMessage: true,
  })
);

export const enableTwoFactorApi = createApiCall<TwoFactorCodeBody>(
  'ENABLE_TWO_FACTOR',
  body => ({
    endpoint: `api/admin/account/two-factor/enable`,
    method: 'POST',
    body,
    skipDefaultErrorMessage: true,
  })
);

export const disableTwoFactorApi = createApiCall<TwoFactorCodeBody>(
  'DISABLE_TWO_FACTOR',
  body => ({
    endpoint: `api/admin/account/two-factor/disable`,
    method: 'POST',
    body,
    skipDefaultErrorMessage: true,
  })
);

const errorMessages: Record<string, string> = {
  invalid_two_factor_code: 'Mã xác thực không đúng.',
  two_factor_too_many_attempts:
    'Bạn đã nhập sai quá nhiều lần, vui lòng thử lại sau.',
  two_factor_already_enabled: 'Xác thực hai lớp đã được bật.',
  two_factor_not_enabled: 'Xác thực hai lớp chưa được bật.',
  two_factor_setup_not_started:
    'Phiên cài đặt đã hết hạn, vui lòng bắt đầu lại.',
  two_factor_required: 'Vai trò của bạn bắt buộc bật xác thực hai lớp.',
  invalid_login_challenge:
    'Phiên đăng nhập đã hết hạn, vui lòng đăng nhập lại.',
};

// The error_code of a failed api call (see lib/api-call), if any.
export const errorCodeOf = (err: any): string | undefined =>
  err?.response?.error_code;

// A Vietnamese message for a failed 2FA api call.
export const twoFactorErrorMessage = (err: any): string => {
  if (err?.status === 429) {
    return 'Bạn thao tác quá nhanh, vui lòng thử lại sau ít phút.';
  }
  const code = errorCodeOf(err);
  if (code && errorMessages[code]) {
    return errorMessages[code];
  }
  return `Lỗi: ${err?.response?.error || code || err?.message || 'Không xác định'}`;
};
